<template>
  <div
    class="amprod-cart-container"
    :class="{ 'dropdown-mode': isDropdownMode }"
  >
    <ErrorMessage v-if="errorMessage" :message="errorMessage" />

    <div
      v-if="!errorMessage"
      class="amprod-cart-content"
      :class="{ 'dropdown-mode': isDropdownMode }"
    >
      <CartHeader
        v-if="showHeader"
        :total-items="totalItems"
        :item-count="cartItems.length"
        @clear-cart="clearCart"
      />

      <CartEmptyState v-if="cartItems.length === 0" />

      <div v-else class="amprod-cart-items">
        <CartItem
          v-for="(item, index) in cartItems"
          :key="item.id || index"
          :item="item"
          @remove-item="removeItem(index)"
          @edit-item="editItem"
        />
      </div>

      <CartActions
        v-if="cartItems.length > 0 && showActions"
        @continue-shopping="$emit('continue-shopping')"
        @request-quote="requestQuote"
      />
    </div>
  </div>
</template>

<script>
import ErrorMessage from "./cart/ErrorMessage.vue";
import CartHeader from "./cart/CartHeader.vue";
import CartEmptyState from "./cart/CartEmptyState.vue";
import CartItem from "./cart/CartItem.vue";
import CartActions from "./cart/CartActions.vue";

export default {
  name: "Cart",
  components: {
    ErrorMessage,
    CartHeader,
    CartEmptyState,
    CartItem,
    CartActions,
  },
  props: {
    apiBaseUrl: {
      type: String,
      default: "",
    },
    showHeader: {
      type: Boolean,
      default: true,
    },
    showActions: {
      type: Boolean,
      default: true,
    },
    isDropdownMode: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["continue-shopping", "quote-requested"],
  data() {
    return {
      cartItems: [],
      errorMessage: null,
    };
  },
  computed: {
    totalItems() {
      return this.cartItems.reduce((total, item) => {
        if (item.options && Array.isArray(item.options)) {
          return (
            total +
            item.options.reduce((optionTotal, option) => {
              return optionTotal + (option.quantity || 1);
            }, 0)
          );
        }
        // Fallback for items without options or with quantity directly on item
        return total + (item.quantity || 1);
      }, 0);
    },
  },
  mounted() {
    this.loadCart();
  },
  methods: {
    loadCart() {
      this.errorMessage = null;

      try {
        // Load from localStorage only
        const localCart = localStorage.getItem("amprod_cart");
        if (localCart) {
          this.cartItems = JSON.parse(localCart);
        } else {
          this.cartItems = [];
        }
      } catch (error) {
        console.error("Error loading cart:", error);
        this.errorMessage = "Failed to load cart data.";
        this.cartItems = [];
      }
    },

    removeItem(index) {
      if (
        confirm("Are you sure you want to remove this item from your cart?")
      ) {
        // Use global cart method
        window.amprodCart.removeCartItem(index);
      }
    },

    editItem(item) {
      if (item.configUrl) {
        // Add the edit parameter to the URL
        const editUrl = new URL(item.configUrl);
        editUrl.searchParams.set("edit_config", item.id);
        window.location.href = editUrl.toString();
      } else {
        alert("Configuration URL not available for this item.");
      }
    },

    clearCart() {
      if (confirm("Are you sure you want to clear your entire cart?")) {
        // Use global cart method
        window.amprodCart.clearCart();
      }
    },

    performCartClear() {
      // Clear cart items array
      this.cartItems = [];

      // Clear localStorage
      try {
        localStorage.removeItem("amprod_cart");
      } catch (error) {
        console.error("Error clearing localStorage:", error);
      }

      // Clear any error messages
      this.errorMessage = null;

      console.log("AmProd Cart: Cart cleared successfully");
    },

    async saveCart() {
      try {
        // Save to localStorage only
        this.saveToLocalStorage();
      } catch (error) {
        console.error("Error saving cart:", error);
        this.errorMessage = "Failed to save cart. Please try again.";
      }
    },

    saveToLocalStorage() {
      try {
        localStorage.setItem("amprod_cart", JSON.stringify(this.cartItems));
      } catch (error) {
        console.error("Error saving to localStorage:", error);
      }
    },

    addItem(item) {
      // Check if this is a standalone option and if it already exists in cart
      if (item.isStandalone && item.type === "option" && item.options?.[0]) {
        const partNumber = item.options[0].part_number;

        // Find existing standalone option with same part number
        const existingItemIndex = this.cartItems.findIndex(
          (cartItem) =>
            cartItem.isStandalone &&
            cartItem.type === "option" &&
            cartItem.options?.[0]?.part_number === partNumber
        );

        if (existingItemIndex !== -1) {
          // Increment quantity of existing item
          const existingItem = this.cartItems[existingItemIndex];
          const quantityToAdd = item.options[0].quantity || 1;

          // Update the quantity in the options array
          existingItem.options[0].quantity =
            (existingItem.options[0].quantity || 1) + quantityToAdd;

          this.saveCart();
          return;
        }
      }

      // Add item to cart with simplified structure
      const cartItem = {
        id: Date.now() + Math.random(), // Simple ID generation
        product: item.product,
        options: item.options || [],
        quantity: item.quantity || 1,
        configUrl: item.configUrl || window.location.href, // Store current URL
        isStandalone: item.isStandalone || false, // Preserve standalone flag
        type: item.type || "product", // Preserve item type
      };

      this.cartItems.push(cartItem);
      this.saveCart();
    },

    updateItem(configId, updatedItem) {
      // Find and update existing cart item
      const itemIndex = this.cartItems.findIndex((item) => item.id == configId);

      console.log(this.cartItems, configId, updatedItem);
      console.log("Updating item at index:", itemIndex, updatedItem);
      if (itemIndex !== -1) {
        // Update the existing item but preserve the ID and configUrl
        this.cartItems[itemIndex] = {
          ...this.cartItems[itemIndex],
          product: updatedItem.product,
          options: updatedItem.options || [],
          quantity: updatedItem.quantity || 1,
        };
        this.saveCart();
        return true;
      }
      return false;
    },

    requestQuote() {
      if (this.cartItems.length === 0) {
        alert("Your cart is empty. Please add some items first.");
        return;
      }

      // Redirect to the quote form URL with cart data
      const quoteFormUrl = window.amprodCartData?.quoteFormUrl || "";
      if (!quoteFormUrl) {
        alert("Quote form URL is not configured.");
        return;
      }

      window.location.href = quoteFormUrl;
    },
  },
};
</script>

<style scoped>
.amprod-cart-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
}

.amprod-cart-items {
  margin-bottom: 30px;
}

/* Dropdown mode adjustments */
.amprod-cart-content.dropdown-mode .amprod-cart-container {
  max-width: none !important;
  margin: 0 !important;
  padding: 16px !important;
  width: 100%;
  box-sizing: border-box;
}

.amprod-cart-content.dropdown-mode .amprod-cart-items {
  margin-bottom: 0 !important;
}

/* Ensure the root container doesn't have default styling in dropdown mode */
.dropdown-mode {
  padding: 0 !important;
  margin: 0 !important;
  max-width: none !important;
}

/* Responsive design */
@media (max-width: 768px) {
  .amprod-cart-container {
    padding: 15px;
  }
}
</style>
